import React, { useState } from "react";
import { View, ScrollView } from "react-native";
import { FileText } from "lucide-react-native";
import {
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { DateField } from "@louise/moon-ui";
import { useFertilityTreatments } from "../FertilityTreatmentsContext";
import { getNextTreatmentScreen } from "../getNextTreatmentScreen";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { useTranslations } from "@src/packages/i18n";
import type { FertilityTreatmentsStackParamList } from "../FertilityTreatmentsNavigator";

export function OtherTreatmentsScreen({
  mode = "other",
}: {
  mode?: "other" | "timed_intercourse" | "unknown";
}) {
  const t = useTranslations();
  const { state, updateState } = useFertilityTreatments();
  const navigation =
    useNavigation<NavigationProp<FertilityTreatmentsStackParamList>>();

  const existing = state.treatments.find(
    (treatment) => treatment.type === mode,
  );
  const treatmentTitle =
    mode === "timed_intercourse"
      ? t("med_history.fertility.treatments.other.timed_intercourse_details")
      : mode === "unknown"
        ? t("med_history.fertility.treatments.other.unknown_treatment_details")
        : t("med_history.fertility.treatments.other.title");
  const [name, setName] = useState(existing?.name ?? "");
  const [date, setDate] = useState(existing?.date ?? "");
  const [notes, setNotes] = useState(existing?.notes ?? "");

  const onNext = () => {
    // Filter out existing treatments of this type, then add new ones
    const otherTreatments = state.treatments.filter((t) => t.type !== mode);
    const nextTreatments = [
      ...otherTreatments,
      {
        ...(existing?.id ? { id: existing.id } : {}),
        type: mode,
        name: name || null,
        date: date || null,
        notes: notes || null,
      },
    ];
    updateState({
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    const next = getNextTreatmentScreen({
      ...state,
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    navigation.navigate(next as keyof FertilityTreatmentsStackParamList);
  };

  return (
    <QuestionLayout
      title={treatmentTitle}
      subtitle={t("med_history.fertility.treatments.other.subtitle")}
      onBack={() => navigation.goBack()}
      onSave={onNext}
      saveLabel={t("med_history.fertility.continue")}
    >
      <ScrollView contentContainerStyle={{ paddingBottom: 24 }}>
        <View className="gap-4">
          <MedicalHistoryTextField
            label={t("med_history.fertility.treatments.other.name_label")}
            placeholder={t(
              "med_history.fertility.treatments.other.name_placeholder",
            )}
            value={name}
            onChangeText={setName}
            style={{ width: "100%" }}
          />
          <DateField
            label={t("med_history.fertility.treatments.other.date_label")}
            value={date}
            onChange={setDate}
          />
          <MedicalHistoryTextField
            label={t("med_history.fertility.treatments.other.notes_label")}
            placeholder={t(
              "med_history.fertility.treatments.other.notes_placeholder",
            )}
            icon={FileText}
            value={notes}
            onChangeText={setNotes}
            style={{ width: "100%" }}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
        </View>
      </ScrollView>
    </QuestionLayout>
  );
}

export default OtherTreatmentsScreen;
